/**
 * DeliveryPreview — Компонент предпросмотра доставки элемента очереди рассылок
 * Дата: 14.02.2026
 *
 * Показывает:
 * - Какой текст будет отправлен (из шаблона с подставленными переменными)
 * - Через какой канал пойдёт доставка (resolved channel)
 * - Почему именно этот канал (причина)
 * - Полную цепочку fallback с визуализацией доступности
 * - Кнопки шаблона (если TG Bot)
 *
 * СВЯЗАННЫЕ МОДУЛИ:
 * - frontend/src/api/broadcastQueue.ts — getDeliveryPreview()
 * - frontend/src/types/broadcastQueue.ts — DeliveryPreview, FallbackStep
 * - internal/domain/service/broadcast/delivery_preview.go — бэкенд resolver
 * - frontend/src/pages/BroadcastQueue.tsx — использует компонент
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { broadcastQueueApi } from '@/api/broadcastQueue';
import { X, CheckCircle2, XCircle, AlertCircle, ChevronRight, MessageSquare, Send, RefreshCw } from 'lucide-react';
import type { DeliveryPreview as DeliveryPreviewType, ChannelOption } from '@/types/broadcastQueue';
import { TestCloneSection } from '@/components/features/broadcast/TestCloneSection';
import { getChannelDisplayName, getChannelIcon } from '@/config/channels';
import { HelpTooltip } from '@/components/ui/help';

// ═══════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════

const CHANNEL_COLORS: Record<string, string> = {
  whatsapp_waba: 'bg-green-50 text-green-700 border-green-200',
  whatsapp_regular: 'bg-green-50 text-green-700 border-green-200',
  telegram_bot: 'bg-blue-50 text-blue-700 border-blue-200',
  telegram_client: 'bg-sky-50 text-sky-700 border-sky-200',
  max: 'bg-purple-50 text-purple-700 border-purple-200',
  sms: 'bg-amber-50 text-amber-700 border-amber-200',
  none: 'bg-neutral-50 text-neutral-400 border-neutral-200',
};

function extractApiError(error: unknown, fallback: string): string {
  const maybeAxios = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };

  return (
    maybeAxios.response?.data?.error ||
    maybeAxios.response?.data?.message ||
    maybeAxios.message ||
    fallback
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════════

/** Бейдж канала */
function ChannelBadge({ channel, large }: { channel: string; large?: boolean }) {
  const icon = getChannelIcon(channel);
  const name = getChannelDisplayName(channel);
  const colors = CHANNEL_COLORS[channel] || 'bg-neutral-50 text-neutral-600 border-neutral-200';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border font-medium ${colors} ${
        large ? 'px-3 py-1.5 text-sm' : 'px-2 py-0.5 text-xs'
      }`}
    >
      <span>{icon}</span>
      <span>{name}</span>
    </span>
  );
}

/** Секция: Финальный текст сообщения */
function MessageSection({ preview }: { preview: DeliveryPreviewType }) {
  const isButtonChannel =
    (preview.resolved_channel === 'telegram_bot' || preview.resolved_channel === 'max') &&
    preview.buttons &&
    preview.buttons.length > 0;
  const buttonChannelLabel = preview.resolved_channel === 'max' ? 'MAX Messenger' : 'Telegram Bot';
  const previewActionUrls = preview.preview_action_urls?.length
    ? preview.preview_action_urls
    : preview.preview_action_url
      ? [preview.preview_action_url]
      : [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
        <MessageSquare className="h-4 w-4" />
        <span>Текст сообщения</span>
        {preview.template_name && (
          <span className="text-xs font-normal text-neutral-400">
            ({preview.template_name})
          </span>
        )}
      </div>

      {/* Основной текст */}
      <div className="rounded-lg bg-neutral-50 p-4 text-sm leading-relaxed whitespace-pre-wrap border border-neutral-200">
        {preview.final_message_text}
      </div>

      {previewActionUrls.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="text-xs font-medium text-blue-700">
            {previewActionUrls.length > 1 ? 'Preview okdoc.su' : 'Preview okdoc.su'}
          </div>
          <div className="mt-2 space-y-2">
            {previewActionUrls.map((url, index) => (
              <div key={url}>
                {previewActionUrls.length > 1 && (
                  <div className="text-[11px] font-medium uppercase tracking-wide text-blue-600">
                    {index === 0 ? 'Основная ссылка' : `Ссылка ${index + 1}`}
                  </div>
                )}
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="block break-all text-sm text-blue-700 underline"
                >
                  {url}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 15.03.26 - Для MAX тоже показываем native inline buttons preview, а не text alternative. */}
      {isButtonChannel && preview.buttons && preview.buttons.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-neutral-500">Кнопки ({buttonChannelLabel}):</div>
          <div className="flex flex-wrap gap-2">
            {preview.buttons.map((btn, i) => (
              <span
                key={i}
                className="inline-flex items-center rounded-md bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 border border-blue-200"
              >
                {btn.text}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Альтернативный текст (если канал не поддерживает кнопки) */}
      {!isButtonChannel && preview.rendered_content !== preview.rendered_text_alternative && preview.rendered_text_alternative && (
        <div className="text-xs text-neutral-400 italic">
          Текстовая альтернатива (без кнопок): {preview.rendered_text_alternative.substring(0, 100)}...
        </div>
      )}

      {/* Шаблон не активен */}
      {!preview.is_active && preview.template_name !== '(шаблон не найден)' && (
        <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 rounded-md px-3 py-1.5 border border-amber-200">
          <AlertCircle className="h-3.5 w-3.5" />
          Шаблон деактивирован
        </div>
      )}
    </div>
  );
}

/** Секция: Resolved канал */
function ResolvedChannelSection({ preview }: { preview: DeliveryPreviewType }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
        <Send className="h-4 w-4" />
        <span>Канал доставки</span>
      </div>

      <div className="flex items-center gap-3">
        <ChannelBadge channel={preview.resolved_channel} large />
        <div className="text-xs text-neutral-500">
          {preview.resolved_channel_reason}
        </div>
      </div>

      {/* Preferred channel */}
      {preview.patient_preferred_channel && (
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <span>Предпочтительный канал пациента:</span>
          <ChannelBadge channel={preview.patient_preferred_channel} />
        </div>
      )}
    </div>
  );
}

/** Секция: Смена канала (12.03.2026) */
function ChannelSwitcherSection({
  itemId,
  tenantId,
  currentChannel,
  onChannelChanged,
}: {
  itemId: string;
  tenantId?: string;
  currentChannel: string;
  onChannelChanged: () => void | Promise<void>;
}) {
  const [isChanging, setIsChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data: channelsData, isLoading: loadingChannels } = useQuery({
    queryKey: ['available-channels', itemId, tenantId],
    queryFn: () => broadcastQueueApi.getAvailableChannels(itemId, tenantId),
    enabled: !!itemId,
    staleTime: 30_000,
  });

  const handleChangeChannel = async (newChannel: string) => {
    if (newChannel === currentChannel) return;

    setIsChanging(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await broadcastQueueApi.changeChannel(itemId, newChannel, tenantId);
      if (result.success) {
        setSuccess(result.message);
        await onChannelChanged();
      } else {
        setError(result.error || 'Ошибка при смене канала');
      }
    } catch (e) {
      setError(extractApiError(e, 'Ошибка при смене канала'));
    } finally {
      setIsChanging(false);
    }
  };

  if (loadingChannels) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
          <span>Сменить канал</span>
          <HelpTooltip
            side="right"
            content="Если сообщение ещё не отправлено, канал просто поменяется. Если уже отправляли или была ошибка, система подготовит повторную отправку через новый канал."
          />
        </div>
        <div className="h-8 w-full animate-pulse rounded bg-neutral-100" />
      </div>
    );
  }

  const channels = channelsData?.channels || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
        <span>Сменить канал</span>
        <HelpTooltip
          side="right"
          content="Здесь можно поменять канал перед отправкой или подготовить повторную отправку через другой канал, если пациент не ответил в первом."
        />
      </div>
      {/* 15.03.26 - Здесь даём сменить только уже зафиксированный queue-channel, а не пересчитываем preview fallback автоматически. */}

      <select
        value={currentChannel}
        onChange={(e) => handleChangeChannel(e.target.value)}
        disabled={isChanging}
        className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
      >
        {channels.map((ch: ChannelOption) => (
          <option
            key={ch.channel}
            value={ch.channel}
            disabled={!(ch.change_available ?? ch.available) && !ch.current}
          >
            {getChannelIcon(ch.channel)} {ch.display_name}
            {ch.current && ' (текущий)'}
            {!(ch.change_available ?? ch.available) && (ch.change_reason || ch.reason)
              ? ` — ${ch.change_reason || ch.reason}`
              : ''}
          </option>
        ))}
      </select>

      {isChanging && (
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Смена канала...
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-700">
          {success}
        </div>
      )}

      <div className="text-xs text-neutral-400">
        Если сообщение ещё не отправлено, канал просто поменяется. Если сообщение уже отправляли или оно завершилось ошибкой, будет подготовлена повторная отправка через новый канал.
      </div>
    </div>
  );
}

/** Секция: Цепочка fallback */
function FallbackChainSection({ preview }: { preview: DeliveryPreviewType }) {
  if (!preview.fallback_chain || preview.fallback_chain.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold text-neutral-700">
        Цепочка доставки
      </div>
      {/* 15.03.26 - Цепочка показывает симуляцию delivery policy: какой канал выберется первым и почему остальные пропускаются. */}

      <div className="space-y-1.5">
        {preview.fallback_chain.map((step, i) => (
          <div
            key={step.channel}
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
              step.would_use
                ? 'bg-green-50 border border-green-200'
                : step.available
                ? 'bg-neutral-50 border border-neutral-100'
                : 'bg-neutral-50/50 border border-neutral-100 opacity-60'
            }`}
          >
            {/* Step number */}
            <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-neutral-200 text-xs font-medium text-neutral-600">
              {i + 1}
            </span>

            {/* Status icon */}
            {step.would_use ? (
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-600" />
            ) : step.available ? (
              <AlertCircle className="h-4 w-4 flex-shrink-0 text-neutral-400" />
            ) : (
              <XCircle className="h-4 w-4 flex-shrink-0 text-red-400" />
            )}

            {/* Channel icon + name */}
            <span className="flex-shrink-0">{getChannelIcon(step.channel)}</span>
            <span className={`font-medium ${step.would_use ? 'text-green-700' : 'text-neutral-700'}`}>
              {step.name}
            </span>

            {/* Arrow for resolved */}
            {step.would_use && (
              <ChevronRight className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
            )}

            {/* Reason */}
            <span className="ml-auto text-xs text-neutral-400 truncate max-w-[200px]" title={step.reason}>
              {step.reason}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════

export interface DeliveryPreviewPanelProps {
  /** ID элемента очереди */
  itemId: string;
  /** Tenant ID (для SuperAdmin) */
  tenantId?: string;
  /** Может ли пользователь менять канал доставки */
  canChangeChannel?: boolean;
  /** Callback при закрытии */
  onClose: () => void;
  /** Обновить список очереди после изменений */
  onQueueChanged?: () => void | Promise<void>;
}

/**
 * DeliveryPreviewPanel — Выдвижная панель предпросмотра доставки.
 * Загружает данные из API и отображает полный предпросмотр.
 */
export function DeliveryPreviewPanel({
  itemId,
  tenantId,
  canChangeChannel = false,
  onClose,
  onQueueChanged,
}: DeliveryPreviewPanelProps) {
  const queryClient = useQueryClient();

  const { data: preview, isLoading, error, refetch } = useQuery({
    queryKey: ['delivery-preview', itemId, tenantId],
    queryFn: () => broadcastQueueApi.getDeliveryPreview(itemId, tenantId),
    enabled: !!itemId,
    staleTime: 30_000, // 30 секунд кэш
  });

  const handleChannelChanged = async () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['available-channels', itemId] });
    if (tenantId) {
      queryClient.invalidateQueries({ queryKey: ['delivery-preview', itemId, tenantId] });
    }
    await onQueueChanged?.();
  };

  return (
    <div className="fixed inset-y-0 right-0 z-50 flex">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/20 transition-opacity" onClick={onClose} />

      {/* Panel */}
      <div className="relative ml-auto w-full max-w-md bg-white shadow-xl border-l border-neutral-200 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-neutral-200 bg-white px-5 py-3">
          <h3 className="text-base font-semibold text-neutral-800">
            Предпросмотр доставки
          </h3>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-blue-600" />
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
              Не удалось загрузить предпросмотр: {(error as Error).message}
            </div>
          )}

          {preview && (
            <>
              {/* 1. Текст сообщения + кнопки */}
              <MessageSection preview={preview} />

              {/* Divider */}
              <div className="border-t border-neutral-100" />

              {/* 2. Resolved канал */}
              <ResolvedChannelSection preview={preview} />

              {canChangeChannel && (
                <>
                  {/* Divider */}
                  <div className="border-t border-neutral-100" />

                  {/* 3. Смена канала (12.03.2026) */}
                  <ChannelSwitcherSection
                    itemId={itemId}
                    tenantId={tenantId}
                    currentChannel={preview.resolved_channel}
                    onChannelChanged={handleChannelChanged}
                  />
                </>
              )}

              {/* Divider */}
              <div className="border-t border-neutral-100" />

              {/* 4. Контрольная реплика */}
              <TestCloneSection
                itemId={itemId}
                tenantId={tenantId}
                currentChannel={preview.resolved_channel}
                onSent={handleChannelChanged}
              />

              {/* Divider */}
              <div className="border-t border-neutral-100" />

              {/* 5. Fallback chain */}
              <FallbackChainSection preview={preview} />

              {/* 6. Template info (collapsible) */}
              {preview.template_name && preview.template_name !== '(шаблон не найден)' && (
                <>
                  <div className="border-t border-neutral-100" />
                  <div className="text-xs text-neutral-400 space-y-1">
                    <div>Шаблон: {preview.template_name} ({preview.template_type})</div>
                    {preview.template_id && <div>ID: {preview.template_id}</div>}
                    <div>Статус: {preview.is_active ? 'Активен' : 'Деактивирован'}</div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Inline Channel Badge (для таблицы)
// ═══════════════════════════════════════════════════════════════════════

export interface InlineChannelBadgeProps {
  /** DeliveryPreview для данного элемента */
  preview?: DeliveryPreviewType | null;
  /** Загружается ли preview */
  loading?: boolean;
}

/**
 * InlineChannelBadge — Компактный бейдж канала для отображения в строке таблицы.
 */
export function InlineChannelBadge({ preview, loading }: InlineChannelBadgeProps) {
  if (loading) {
    return <span className="inline-block h-5 w-16 animate-pulse rounded bg-neutral-100" />;
  }

  if (!preview) {
    return <span className="text-xs text-neutral-300">—</span>;
  }

  return <ChannelBadge channel={preview.resolved_channel} />;
}
